import { useContext, useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import clsx from 'clsx';
import * as R from 'ramda';

import AssigneeFilter from '@atom/components/common/assigneeFilter/AssigneeFilter';
import {
  exceedsADOTStock,
  toADOTPositiveQuantity,
} from '@atom/components/common/requests/adotQuantityUtilities';
import ADOTGroupMembersTooltip from '@atom/components/common/requests/viewRequest/adotView/ADOTGroupMembersTooltip';
import { GET_ASSETS } from '@atom/graph/asset';
import { Progress, SelectAutocomplete, Snackbar, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetBudget, AssetConnectionItem } from '@atom/types/asset';
import { AssetRequestCreateInput } from '@atom/types/assetRequest';
import { UserDetail } from '@atom/types/user';
import { UserGroup } from '@atom/types/userGroups';

import RequestsContext from '../../RequestsContext';

import { isADOTApprovalGroup } from './adotCreateUtilities';

import classes from './adotCreate.module.css';

const ASSET_LIMIT = 250;

const styles = {
  label: {
    color: colors.neutral.dim,
  },
};

const getUserSubtext = (user: UserDetail): string =>
  [user.email, user.title].filter(Boolean).join(' • ');

const getGroupSubtext = (group: UserGroup): string => {
  const count = group.userCount ?? 0;
  const users = `${count} ${count === 1 ? 'User' : 'Users'}`;
  const scope = (group.groupPath || []).join(' / ');

  return [users, scope].filter(Boolean).join(' • ');
};

type ADOTTransferCreateProps = {
  budget: AssetBudget;
  createInputs: AssetRequestCreateInput;
  updateCreateInput: (
    value: any,
    property: keyof AssetRequestCreateInput,
  ) => void;
  selectedFromAsset: AssetConnectionItem;
  setSelectedFromAsset: (asset: AssetConnectionItem) => void;
  // TODO: [AM-16707]: Temp code until BE solution is built
  totalRemaining: number;
  fromTotalRemaining: number;
  totalRemainingLoading: boolean;
  fromTotalRemainingLoading: boolean;
};

const ADOTTransferCreate = ({
  budget,
  createInputs,
  updateCreateInput,
  selectedFromAsset,
  setSelectedFromAsset,
  // TODO: [AM-16707]: Temp code until BE solution is built
  totalRemaining,
  fromTotalRemaining,
  totalRemainingLoading,
  fromTotalRemainingLoading,
}: ADOTTransferCreateProps) => {
  const client = useApolloClient();

  const { asset } = useContext(RequestsContext);

  const [assets, setAssets] = useState<AssetConnectionItem[]>([]);
  const [loadingAssets, setLoadingAssets] = useState<boolean>(false);
  // The field keeps the typed text so "0." survives until the decimals land;
  // only the parsed quantity reaches the create input.
  const [quantityText, setQuantityText] = useState<string>(
    createInputs?.quantityOrdered?.toString() ?? '',
  );

  // Transfer From candidates share the item's inventory type AND base unit,
  // never the item itself. The server filters; the client only pages.
  const getAssetPage = (page: number) =>
    client.query({
      query: GET_ASSETS,
      variables: {
        input: {
          page,
          limit: ASSET_LIMIT,
          schemaId: asset.schemaId,
          budget: { unit: budget?.unit },
          omitIds: [asset.id],
        },
      },
      fetchPolicy: 'network-only',
    });

  useEffect(() => {
    const retrieveAssets = async () => {
      setLoadingAssets(true);

      try {
        const { data } = await getAssetPage(1);
        const total = Number(data.assets.totalCount);
        const pages = Math.ceil(total / ASSET_LIMIT);

        const remaining = await Promise.all(
          R.range(2, pages + 1).map(page =>
            getAssetPage(page).then(result => result.data.assets.assets),
          ),
        );

        setAssets([...data.assets.assets, ...R.flatten(remaining)]);
      } catch (err) {
        Snackbar.error({ message: 'An error occurred. Please try again.' });
      } finally {
        setLoadingAssets(false);
      }
    };

    retrieveAssets();
  }, []);

  const handleAssetSelect = (newAsset: AssetConnectionItem) => {
    setSelectedFromAsset(newAsset);
    updateCreateInput(newAsset?.id, 'fromAssetId');
    updateCreateInput([], 'assignedTo');
    updateCreateInput([], 'assignedToGroups');
  };

  const handleQuantityChange = (value: string) => {
    const text = value.replace('-', '');
    setQuantityText(text);
    updateCreateInput(toADOTPositiveQuantity(text), 'quantityOrdered');
  };

  const handleAssigneesChange = (userIds: string[], groupIds: string[]) => {
    updateCreateInput(userIds, 'assignedTo');
    updateCreateInput(groupIds, 'assignedToGroups');
  };

  const renderOption = (option: AssetConnectionItem) => (
    <div>
      <div>{option.name}</div>
      <div
        className={classes.assetOptionSubtext}
      >{`${option.budget?.quantityOnHand ?? 0} ${option.budget?.unit} in stock`}</div>
    </div>
  );

  const requestedQuantityError =
    !!selectedFromAsset &&
    !fromTotalRemainingLoading &&
    exceedsADOTStock(fromTotalRemaining, createInputs?.quantityOrdered);

  return (
    <>
      <div className={classes.inputContainer}>
        <SelectAutocomplete
          id="fromAssetId"
          label="Transfer From"
          options={assets}
          value={selectedFromAsset}
          onChange={(_, newValue) => handleAssetSelect(newValue)}
          renderOption={renderOption}
          getOptionLabel={option => option.name}
          disabled={loadingAssets}
          loading={loadingAssets}
          InputLabelProps={{ style: styles.label }}
          blurOnSelect
          data-cy="adotTransferFromAsset"
        />
        {selectedFromAsset && (
          <div
            className={clsx(classes.subtext, classes.selectedAsset)}
            data-cy="adotTransferFromStock"
          >
            {fromTotalRemainingLoading ? (
              <Progress size={10} style={{ justifyContent: 'flex-start' }} />
            ) : (
              `${fromTotalRemaining} ${selectedFromAsset?.budget?.unit} in stock`
            )}
          </div>
        )}
      </div>
      <div className={classes.inputContainer}>
        <div className={clsx(classes.subtext, classes.title)}>Transfer To</div>
        <div>{asset?.name}</div>
        {totalRemainingLoading ? (
          <Progress size={10} style={{ justifyContent: 'flex-start' }} />
        ) : (
          <div
            className={classes.subtext}
            data-cy="adotTransferToStock"
          >{`${totalRemaining} ${budget?.unit} in stock`}</div>
        )}
      </div>
      <div className={classes.inputContainer}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          type="number"
          value={quantityText}
          label={`Requested Quantity (${budget?.unit})`}
          onChange={event => handleQuantityChange(event.target.value)}
          onBlur={() =>
            setQuantityText(createInputs?.quantityOrdered?.toString() ?? '')
          }
          helperText={
            requestedQuantityError && 'Exceeds available stock levels.'
          }
          error={requestedQuantityError}
          data-cy="adotTransferQuantity"
        />
      </div>
      {createInputs?.quantityOrdered && (
        <div className={classes.quantityTransferSubtext}>
          The unit cost of this inventory item will be re-calculated by taking
          into account the approved quantity and the unit cost of the inventory
          it was transferred from.
        </div>
      )}
      <div className={classes.inputContainer}>
        <AssigneeFilter
          userIds={createInputs?.assignedTo || []}
          groupIds={createInputs?.assignedToGroups || []}
          onChange={handleAssigneesChange}
          getUserSubtext={getUserSubtext}
          getGroupSubtext={getGroupSubtext}
          filterGroups={group =>
            isADOTApprovalGroup(selectedFromAsset?.categories, group)
          }
          renderGroupChip={(chip, group) => (
            <ADOTGroupMembersTooltip groupId={group.id}>
              {chip}
            </ADOTGroupMembersTooltip>
          )}
          dataCy="adotTransferAssignedTo"
        />
        <div className={clsx(classes.subtext, classes.helper)}>
          An email will be sent to notify the assigned users about this transfer
          request.
        </div>
      </div>
    </>
  );
};

export default ADOTTransferCreate;
